// types/protocolConfig.ts
/**
 * Client-side protocol configuration for VR Perimetry clinical trials.
 *
 * Built from the server protocol ruleset (types/protocolRuleset.ts) by
 * config/protocolRulesetAdapter.ts — the server is the source of truth.
 * This type intentionally contains only what the tracker UI consumes.
 */

// ============================================================
// TYPES
// ============================================================

export type BundleScheduleAnchor = 'actual_baseline' | 'rand_date';
export type BundleCollectionAnchor = 'first_test' | 'expected_day';
/** 'pre_anchor': all tests collected strictly BEFORE the anchor day (e.g. DFI
 * pre-dose baseline); the window has no lower bound and closes at the anchor. */
export type BundleWindowMode = 'pre_anchor';

/**
 * Study-level identification (display only)
 */
export interface StudyMetadata {
  /** Protocol identifier — the server ruleset key (e.g., "sienna_v2_0") */
  protocolId: string;

  /** Marketing/common name (e.g., "SIENNA") */
  studyName: string;

  /** Ruleset sign-off status (e.g., 'approved', 'draft'); non-approved rulesets get a DRAFT badge */
  rulesetStatus?: string;

  /** Display label for the schedule-anchor date (e.g., 'D1'); absent = legacy 'Rand Date' wording */
  anchorLabel?: string;
}

/**
 * Enrollment cohorts (display/filter only). Absent = the study has no cohort
 * concept and the report renders no cohort UI.
 */
export interface StudyCohorts {
  /** Cohort B = randomized on/after this date (Y-m-d, ET) */
  bStartDate?: string;
}

/**
 * Study timeline parameters
 */
export interface StudyTimeline {
  /** Total study duration in weeks */
  totalWeeks: number;

  /** Year boundaries for reporting (e.g., Year 1 = weeks 0-52) */
  yearBoundaries: number[]; // [52, 104] means Year 1 ends at 52, Year 2 at 104
}

/**
 * Bundle/home testing configuration
 */
export interface BundleConfig {
  /** Target number of tests per bundle (protocol ideal) */
  targetTestsPerBundle: number;

  /** Minimum tests for bundle to be usable/analyzable */
  minimumValidTests: number;

  /** Bundle collection window in days, measured from first eligible test */
  windowDays: number;

  /** Which participant date anchors visit-window projections */
  scheduleAnchor?: BundleScheduleAnchor;

  /** Default visit window: days before projected milestone day */
  defaultWindowDaysBefore?: number;

  /** Default visit window: days after projected milestone day */
  defaultWindowDaysAfter?: number | null;

  /** Total expected bundles over study duration */
  totalExpectedBundles: number;

  /** Bundle milestone schedule */
  milestones: BundleMilestone[];

  /** Expected gaps where no bundle activity is expected */
  expectedGaps: ExpectedGap[];

  /** Completion tier thresholds */
  completionTiers: {
    /** Tests needed for "Complete" status */
    complete: number;
    /** Tests needed for "Partial" status */
    partialMin: number;
  };
}

/**
 * Individual bundle milestone definition
 */
export interface BundleMilestone {
  /** Bundle number (1-indexed) */
  bundleNumber: number;

  /** Expected start week (relative to baseline) */
  expectedWeek: number;

  /** Expected start day (relative to baseline) for exact day-based schedules */
  expectedDay?: number;

  /** Optional override for visit window days before projected start */
  windowDaysBefore?: number;

  /** Optional override for visit window days after projected start */
  windowDaysAfter?: number | null;

  /** How the 10-day collection window starts for this milestone */
  collectionAnchor?: BundleCollectionAnchor;

  /** Window shape override; 'pre_anchor' = collect strictly before the anchor day */
  windowMode?: BundleWindowMode;

  /** Per-milestone session target override (e.g., DFI EFU bundles target 2) */
  targetSessions?: number;

  /** Per-milestone minimum-valid override (e.g., DFI baseline min 2) */
  minimumValidTests?: number;

  /** Study year (1 or 2, for grouping) */
  studyYear: number;

  /** Associated visit ID for reminders (e.g., "V6") */
  reminderVisitId: string;

  /** Human-readable label (e.g., "Visit 6 (Day 85)") */
  label?: string;

  /** Visit display-name override (e.g., "Baseline Visit 3"); otherwise derived from label */
  visitName?: string;
}

/**
 * Expected gap periods where no bundle is expected
 */
export interface ExpectedGap {
  /** Gap identifier */
  gapId: string;

  /** Human-readable description */
  description: string;

  /** Start week of gap */
  startWeek: number;

  /** End week of gap */
  endWeek: number;

  /** Reason for gap */
  reason: 'primary_endpoint' | 'year_transition' | 'washout' | 'other';

  /** Bundles that should be complete before this gap */
  requiredBundlesBeforeGap: number;
}

/**
 * Report alert thresholds
 */
export interface AlertThresholds {
  /** Days without test activity to trigger "stale" warning */
  staleDays: number;

  /** Days remaining in window to trigger "expiring soon" warning */
  expiringWarningDays: number;
}

// ============================================================
// MASTER PROTOCOL CONFIG
// ============================================================

/**
 * Complete protocol configuration object
 */
export interface ProtocolConfig {
  metadata: StudyMetadata;
  timeline: StudyTimeline;
  bundles: BundleConfig;
  alerts: AlertThresholds;
  /** Absent = no cohort UI in the report */
  cohorts?: StudyCohorts;
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Returns the 0-indexed day offset from baseline for date math (use with addDays).
 * Config stores protocol days (1-indexed: D1 = baseline). This converts to offset.
 */
export const getBundleMilestoneExpectedDay = (milestone: BundleMilestone): number => {
  if (milestone.expectedDay !== undefined) {
    return milestone.expectedDay - 1; // Protocol day → 0-indexed offset
  }
  return milestone.expectedWeek * 7; // Already a 0-indexed offset
};

/**
 * Returns the 1-indexed protocol day number (D1 = baseline) for display purposes.
 */
export const getBundleMilestoneProtocolDay = (milestone: BundleMilestone): number => {
  return milestone.expectedDay ?? milestone.expectedWeek * 7 + 1;
};

/**
 * Bounded lookback for pre-anchor windows. The server engine has NO lower
 * bound (every home test before the anchor counts); the client needs a finite
 * day offset for date math, so use a lookback comfortably longer than any
 * screening period.
 */
export const PRE_ANCHOR_LOOKBACK_DAYS = 365;

/** Per-milestone session target, falling back to the protocol-wide target */
export const getBundleMilestoneTargetSessions = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number => {
  return milestone.targetSessions ?? config.bundles.completionTiers.complete;
};

/** Per-milestone minimum-valid count, falling back to the protocol-wide minimum */
export const getBundleMilestoneMinimumValidTests = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number => {
  return milestone.minimumValidTests ?? config.bundles.completionTiers.partialMin;
};

export const getBundleMilestoneWindowDaysBefore = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number => {
  return milestone.windowDaysBefore ?? config.bundles.defaultWindowDaysBefore ?? 0;
};

export const getBundleMilestoneWindowDaysAfter = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number | null => {
  if (milestone.windowDaysAfter !== undefined) {
    return milestone.windowDaysAfter;
  }

  return config.bundles.defaultWindowDaysAfter ?? null;
};

export const getBundleMilestoneWindowStartDay = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number => {
  if (milestone.windowMode === 'pre_anchor') {
    return -PRE_ANCHOR_LOOKBACK_DAYS;
  }
  return Math.max(
    0,
    getBundleMilestoneExpectedDay(milestone) - getBundleMilestoneWindowDaysBefore(config, milestone)
  );
};

export const getBundleMilestoneWindowEndDay = (
  config: ProtocolConfig,
  milestone: BundleMilestone
): number | null => {
  if (milestone.windowMode === 'pre_anchor') {
    return -1; // last collectable day is the day before the anchor
  }
  const daysAfter = getBundleMilestoneWindowDaysAfter(config, milestone);
  if (daysAfter === null) {
    return null;
  }

  return getBundleMilestoneExpectedDay(milestone) + daysAfter;
};

export const getBundleMilestoneCollectionAnchor = (
  milestone: BundleMilestone
): BundleCollectionAnchor => {
  return milestone.collectionAnchor ?? 'first_test';
};

/**
 * Visit display name for a milestone: the ruleset's visit_label override when
 * present, otherwise derived from the label (e.g., "Visit 6 (Day 85)" →
 * "Visit 6"); falls back to the visit ID.
 */
export const getBundleMilestoneVisitName = (milestone: BundleMilestone): string => {
  if (milestone.visitName) return milestone.visitName;
  const fromLabel = milestone.label?.replace(/\s*\(Day \d+\)\s*$/, '').trim();
  return fromLabel || milestone.reminderVisitId;
};

export const getBundleScheduleAnchorDate = (
  config: ProtocolConfig,
  randDate: Date | null,
  actualBaselineDate: Date | null
): Date | null => {
  return config.bundles.scheduleAnchor === 'actual_baseline'
    ? actualBaselineDate || randDate
    : randDate || actualBaselineDate;
};

/**
 * Get expected bundles by study day
 */
export const getExpectedBundleCountByDay = (config: ProtocolConfig, studyDay: number): number => {
  return config.bundles.milestones.filter(
    milestone => getBundleMilestoneExpectedDay(milestone) <= studyDay
  ).length;
};

/**
 * Get the study year for a given week
 */
export const getStudyYear = (config: ProtocolConfig, studyWeek: number): number => {
  const { yearBoundaries } = config.timeline;
  for (let i = 0; i < yearBoundaries.length; i++) {
    if (studyWeek <= yearBoundaries[i]) {
      return i + 1;
    }
  }
  return yearBoundaries.length + 1; // Beyond defined years
};
